// src/pages/SearchResults.js
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Divider,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import Navbar from '../components/Navbar';
import HeroBanner from '../components/HeroBanner';
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';
import { newsItems, eventItems, refItems, tutItems, modelItems } from '../data/SiteData';
import { useLocation, useNavigate } from 'react-router-dom';

const SearchResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const query = params.get('q') || '';

  const [searchInput, setSearchInput] = useState(query);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(searchInput)}`);
  };

  const breadcrumbItems = [
    { title: 'Home', path: '/' },
    { title: 'Search Results', path: '/search' },
  ];

  // Accordion expanded state
  const [expanded, setExpanded] = useState(false);
  const handleAccordionToggle = () => setExpanded((prev) => !prev);

  // CONTENT filter
  const contentOptions = ['Models', 'Reference Material', 'Videos & Tutorials', 'News', 'Events'];
  const initialContentState = contentOptions.reduce((acc, c) => {
    acc[c.toLowerCase()] = true;
    return acc;
  }, {});
  const [contentChecked, setContentChecked] = useState(initialContentState);

  const handleContentChange = (content) => {
    const key = content.toLowerCase();
    setContentChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const clearAllContent = () => {
    const cleared = contentOptions.reduce((acc, c) => ({ ...acc, [c.toLowerCase()]: false }), {});
    setContentChecked(cleared);
  };

  // Sort
  const [sortOrder, setSortOrder] = useState('Latest');

  // Combine all items with a contentType property
  const allItems = useMemo(() => {
    return [
      ...modelItems.map((i) => ({ ...i, contentType: 'Models' })),
      ...refItems.map((i) => ({ ...i, contentType: 'Reference Material' })),
      ...tutItems.map((i) => ({ ...i, contentType: 'Videos & Tutorials' })),
      ...newsItems.map((i) => ({ ...i, contentType: 'News' })),
      ...eventItems.map((i) => ({ ...i, contentType: 'Events' })),
    ];
  }, []);

  // Filtering logic
  const filteredItems = useMemo(() => {
    return allItems
      .filter((item) => {
        // Search filter
        const matchQuery =
          item.title.toLowerCase().includes(query.toLowerCase()) ||
          (item.description?.toLowerCase().includes(query.toLowerCase())) ||
          (item.mainContent?.toLowerCase().includes(query.toLowerCase()));
        if (!matchQuery) return false;

        // Content filter
        const contentKey = item.contentType.toLowerCase();
        return contentChecked[contentKey];
      })
      .sort((a, b) => {
        if (sortOrder === 'Latest') return new Date(b.date) - new Date(a.date);
        return new Date(a.date) - new Date(b.date);
      });
  }, [query, contentChecked, sortOrder, allItems]);

  const CustomCheckboxIcon = () => (
    <Box
      sx={{
        width: 18,
        height: 18,
        border: '1px solid #000',
        backgroundColor: '#fff',
      }}
    />
  );

  const CustomCheckboxCheckedIcon = () => (
    <Box
      sx={{
        width: 18,
        height: 18,
        border: '1px solid #000',
        backgroundColor: '#489596',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&::after': {
          content: '""',
          width: 6,
          height: 10,
          border: 'solid #fff',
          borderWidth: '0 2px 2px 0',
          transform: 'rotate(45deg)',
        },
      }}
    />
  );

  return (
    <>
      <Navbar />
      <HeroBanner
        heroTitle="Search Results"
        heroDescription={`Showing results for "${query}"`}
        imageUrl="/images/digiTwins.png"
        height="165px"
      />
      <Breadcrumb items={breadcrumbItems} />

      <Box sx={{ padding: '32px', maxWidth: '1300px', margin: '0 auto' }}>
        
        {/* SEARCH BAR ABOVE BOTH COLUMNS */}
<Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
  <Typography
    sx={{
      fontFamily: 'Poppins, sans-serif',
      fontSize: '24px',
      fontWeight: 400,
      color: '#142434',
      whiteSpace: 'nowrap', // prevent wrapping
    }}
  >
    Search:
  </Typography>

  <Box sx={{ flex: 1 }}>
    <form onSubmit={handleSearchSubmit}>
      <TextField
        fullWidth
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        placeholder="Type to search..."
        variant="outlined"
        size="small"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton type="submit" edge="end">
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </form>
  </Box>
</Box>


        {/* COLUMNS */}
        <Box sx={{ display: 'flex', gap: '32px' }}>
          {/* LEFT COLUMN */}
          <Box
            sx={{
              width: '248px',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              overflow: 'hidden',
              height: 'fit-content',
            }}
          >
            {/* HEADER */}
            <Box
              sx={{
                backgroundColor: 'rgba(72,149,150,0.1)',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                px: 2,
              }}
            >
              <Typography
                sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '20px', fontWeight: 400, color: '#142434' }}
              >
                Refine Results
              </Typography>
            </Box>

            {/* CONTENT ACCORDION */}
            <Accordion
              expanded={expanded}
              onChange={handleAccordionToggle}
              disableGutters
              square
              sx={{ border: '1px solid #e0e0e0', '&:before': { display: 'none' } }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ backgroundColor: '#fff', '& .MuiAccordionSummary-content': { margin: 0 }, px: 2, py: 1.5 }}
              >
                <Typography sx={{ fontFamily: 'PT Sans', fontSize: '18px', fontWeight: 400, color: '#142434' }}>
                  CONTENT
                </Typography>
              </AccordionSummary>
              <Divider />
              <AccordionDetails
                sx={{
                  px: 2,
                  backgroundColor: '#F3F3F4',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                {contentOptions.map((c) => (
                  <FormControlLabel
                    key={c}
                    control={
                      <Checkbox
                        checked={contentChecked[c.toLowerCase()]}
                        onChange={() => handleContentChange(c)}
                        icon={<CustomCheckboxIcon />}
                        checkedIcon={<CustomCheckboxCheckedIcon />}
                      />
                    }
                    label={c}
                    sx={{ fontFamily: 'PT Sans', fontSize: '15px' }}
                  />
                ))}
                <Typography
                  onClick={clearAllContent}
                  sx={{
                    fontFamily: 'PT Sans',
                    fontSize: '18px',
                    textDecoration: 'underline',
                    color: '#489596',
                    cursor: 'pointer',
                    mt: 1,
                  }}
                >
                  Clear all
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* RIGHT COLUMN */}
          <Box sx={{ flex: 1 }}>
            {/* RESULTS HEADER */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography sx={{ fontFamily: 'PT Sans', fontSize: '16px', color: '#142434' }}>
                {filteredItems.length} Results
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontFamily: 'PT Sans', fontSize: '16px', color: '#142434' }}>Sort:</Typography>
                <Select
                  size="small"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  sx={{ width: '100px', height: '32px', backgroundColor: '#fff' }}
                >
                  <MenuItem value="Latest">Latest</MenuItem>
                  <MenuItem value="Oldest">Oldest</MenuItem>
                </Select>
              </Box>
            </Box>

            {/* EMPTY STATE */}
            {filteredItems.length === 0 && (
              <Typography
                sx={{
                  fontFamily: 'PT Sans',
                  fontSize: '20px',
                  fontStyle: 'italic',
                  color: '#999C9F',
                  pl: 5,
                  mb: 2,
                }}
              >
                No results have been found. Please edit your filters on the left.
              </Typography>
            )}

            {/* RESULTS */}
            {filteredItems.map((item) => (
              <Card
                key={item.title + item.contentType}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  mb: 3,
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  boxShadow: 'none',
                  p: 2,
                }}
              >
                {item.image && (
                  <Box component="a" href={item.link} sx={{ display: 'block', mr: 2 }}>
                    <Box
                      component="img"
                      src={item.image}
                      alt={item.title}
                      sx={{ width: '120px', height: 'auto', borderRadius: '4px', objectFit: 'cover' }}
                    />
                  </Box>
                )}

                <CardContent sx={{ padding: 0 }}>
                  <Typography
                    component="a"
                    href={item.link}
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '18px',
                      fontWeight: 500,
                      color: '#489596',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      mb: '4px',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {item.title}
                  </Typography>

                  {item.date && (
                    <Typography sx={{ fontFamily: 'PT Sans', fontSize: '14px', color: '#142434', mb: 1 }}>
                      {item.date}
                    </Typography>
                  )}

                  <Typography sx={{ fontFamily: 'PT Sans', fontSize: '15px', color: '#142434', lineHeight: 1.4 }}>
                    {item.description || item.mainContent}
                  </Typography>

                  <Typography sx={{ fontFamily: 'PT Sans', fontSize: '13px', color: '#999C9F', mt: 1 }}>
                    {item.contentType}
                  </Typography>
                </CardContent>
              </Card>
            ))}

            <Typography sx={{ fontFamily: 'PT Sans', fontSize: '16px', color: '#142434' }}>
              {filteredItems.length} Results
            </Typography>
          </Box>
        </Box>
      </Box>

      <Footer />
    </>
  );
};

export default SearchResults;
